import type { InferType } from 'yup';
import * as Yup from 'yup';

/** Los cinco slots de ArticleCta::SLOTS (R7). */
export const ARTICLE_CTA_SLOT_VALUES = [
  'right_rail',
  'in_body_mid',
  'sticky_mobile',
  'in_body_top',
  'end_of_article',
] as const;

export const ARTICLE_CTA_RENDER_TYPES = ['standard', 'poll'] as const;

/**
 * Un placeholder que el servidor resuelve en el request (R5.1), p. ej.
 * `{prize_image}` o `/tienda/{product_slug}`. Se detecta por la forma, no por
 * una lista de nombres: una variante nueva no toca esta validacion.
 */
export const PLACEHOLDER_PATTERN = /\{[a-z_]+\}/;

/** Prefijos de idioma que un destino interno no puede traer: los pone el servidor. */
const LANG_PREFIX_PATTERN = /^\/(es|en|pt)(\/|$)/;

/**
 * Un destino externo se sirve tal cual: sin prefijo de idioma y sin nuestras
 * UTMs, que pisarian las que trae la URL. En la app abre en pestania nueva.
 */
const EXTERNAL_PATTERN = /^https?:\/\//i;

/** Interno: una barra y algo mas. `//host` no entra: el navegador lo resuelve como URL. */
const INTERNAL_PATTERN = /^\/(?!\/)/;

const NON_MEASURABLE_EXTENSIONS = /\.(svg|gif)(\?|#|$)/i;

export const hasPlaceholder = (value?: string | null) =>
  PLACEHOLDER_PATTERN.test(value || '');

/** Una URL literal es la que se puede medir: la del placeholder no existe aun. */
export const isLiteralImageUrl = (value?: string | null) =>
  !!value && !hasPlaceholder(value);

/** Las tres claves de idioma de `content`. */
const CONTENT_LANGS = ['es', 'en', 'pt'] as const;

/**
 * `imageWidth`/`imageHeight` describen el ARCHIVO fuente y los llena la medicion
 * del formulario. Hay un solo par para los tres idiomas (R1: "image_url literal
 * en cualquier idioma"), asi que el formulario guarda la medicion mas chica de
 * las que haya cargado: es la que decide si el arte aguanta la caja del slot.
 */
const measuredDimension = (label: string) =>
  Yup
    .number()
    .nullable()
    .transform((_, val) => (val === '' || val === null) ? null : Number(val))
    .test(
      'medida',
      `Falta medir el ${label} de la imagen: pegá de nuevo la URL y esperá a que se mida`,
      function (value) {
        const content = (this.parent as any)?.content;
        const hasLiteralImage = CONTENT_LANGS.some(lang => isLiteralImageUrl(content?.[lang]?.imageUrl));

        return !hasLiteralImage || !!value;
      }
    );

/**
 * El multiselect de team entrega `{value,label}` y el inventario guarda un array
 * plano. Se normaliza aca y no solo al armar el payload, porque la validacion
 * corre antes: sin esto, elegir un idioma o un device hacia fallar el formulario
 * y no se podia guardar la fila.
 */
const multiSelectValues = () =>
  Yup
    .array()
    .transform((_, original) => {
      if (original === null || original === undefined || original === '') return null;
      if (!Array.isArray(original)) return original;

      return original
        .map((item: any) => (typeof item === 'string' ? item : item?.value))
        .filter((value: any) => value !== undefined && value !== null && value !== '');
    })
    .of(Yup.string())
    .nullable();

const optionalString = () =>
  Yup
    .string()
    .transform((_, val) => (val === '' || val === null) ? null : val)
    .nullable();

const imageUrlField = () =>
  optionalString()
    // `naturalWidth` no es confiable en un SVG sin dimensiones intrinsecas, y el
    // GIF animado no entra en una superficie que se mide por Core Web Vitals.
    .test(
      'medible',
      'No se puede usar .svg ni .gif: no se puede medir o no entra en Core Web Vitals',
      value => !value || hasPlaceholder(value) || !NON_MEASURABLE_EXTENSIONS.test(value)
    );

const localizedContent = (lang: string) =>
  Yup.object({
    title: Yup.string().required(`Introduce el título en ${lang}`),
    subtitle: optionalString(),
    ctaLabel: Yup.string().required(`Introduce el texto del botón en ${lang}`),
    imageUrl: imageUrlField(),
    imageAlt: optionalString(),
  }).required();

export const formArticleCtaSchema = Yup.object({
  key: Yup
    .string()
    .matches(/^\S*$/, 'La key no puede contener espacios en blanco')
    .required('Introduce la key del CTA'),
  slot: Yup
    .string()
    .oneOf(ARTICLE_CTA_SLOT_VALUES as unknown as string[], 'Elegí un slot válido')
    .required('Elegí el slot'),
  renderType: Yup
    .string()
    .oneOf(ARTICLE_CTA_RENDER_TYPES as unknown as string[])
    .default('standard')
    .required('Elegí el tipo de render'),
  variant: Yup
    .string()
    .default('A')
    .required('Introduce la variante'),
  experimentKey: optionalString(),
  weight: Yup
    .number()
    .typeError('El peso es un número entre 0 y 100')
    .min(0, 'El peso mínimo es 0')
    .max(100, 'El peso máximo es 100')
    .default(50)
    .required('Introduce el peso'),
  active: Yup
    .boolean()
    .default(true),
  destination: Yup
    .string()
    .test(
      'ruta-o-url',
      'Es una ruta interna que empieza con / o una URL externa con http(s)://',
      value => !value || EXTERNAL_PATTERN.test(value) || INTERNAL_PATTERN.test(value)
    )
    .test(
      'sin-prefijo-de-idioma',
      'Una ruta interna va sin prefijo de idioma: lo agrega el servidor con el idioma del artículo',
      value => EXTERNAL_PATTERN.test(value || '') || !LANG_PREFIX_PATTERN.test(value || '')
    )
    .required('Introduce el destino'),
  startsAt: optionalString(),
  endsAt: optionalString(),
  content: Yup.object({
    es: localizedContent('español'),
    en: localizedContent('inglés'),
    pt: localizedContent('portugués'),
  }).required(),
  targeting: Yup.object({
    tags: multiSelectValues(),
    lang: multiSelectValues(),
    device: multiSelectValues(),
    // El select manda 'any' | 'true' | 'false': 'any' es "sin filtro" y se
    // guarda como null, que es lo que el backend lee como ausente.
    loggedIn: Yup
      .boolean()
      .nullable()
      .default(null)
      .transform((_, val) => (val === 'any' || val === '' || val === null || val === undefined)
        ? null
        : (val === true || val === 'true')),
  }).default({}),
  // Los mide el formulario cuando se pega la URL (R6.1): nunca se tipean. Si hay
  // una URL literal en algun idioma, sin dimensiones no se puede guardar -- es
  // bloqueante y no un warning, porque sin ellas no se sabe si la fuente aguanta
  // la caja del slot.
  imageWidth: measuredDimension('ancho'),
  imageHeight: measuredDimension('alto'),
})
  .required();

export type FormArticleCtaData = InferType<typeof formArticleCtaSchema>;
